<template>
    <div class="flex flex-col space-y-8">
        <div class="select-date-tabs">
            <Tabs teleport="#date-tabs-target" active-tab-class="--active">
                <nav class="grid grid-cols-5 mb-4 w-full gap-2">
                    <Tab v-for="(report, index) of state.reports" :key="report.name" :slug="`${index + 1}`" :name="report.name" :default="index === 0" @click="onTabClicked(report, index)">
                        <div class="space-y-8 w-full">
                            <MainViewCard class="pt-4 pb-2 px-2">
                                <PigsStatusGraph :report-title="report.name" :data="report.graph"></PigsStatusGraph>
                            </MainViewCard>
                            <div class="flex flex-col xl:flex-row space-y-8 xl:space-x-6 xl:space-y-0">
                                <ReportsEntriesTable
                                    :empty-rows="emptyEntryTableRows"
                                    :data="report.entriesTable"
                                    :records="report.records"
                                    :comment-difference="commentDifference"
                                    @fetch-records="fetchRecords"
                                    @update-comment="onCommentUpdated"
                                ></ReportsEntriesTable>
                                <ReportsDeparturesTable
                                    :data="report.departuresTable"
                                    :records="report.records"
                                    :comment-difference="commentDifference"
                                    @fetch-records="fetchRecords"
                                    @update-comment="onCommentUpdated"
                                ></ReportsDeparturesTable>
                            </div>
                        </div>
                    </Tab>
                </nav>
            </Tabs>

            <div id="date-tabs-target" class="flex justify-center w-full" />
        </div>
        <div class="flex flex-col items-end gap-3">
            <template v-if="state.applicableIds.length > 0">
                <SelectFeedReconciliations
                    :reconciliation-ids="state.reconciliationIds"
                    :applicable-ids="state.applicableIds"
                    @update:reconciliation-ids="onReconciliationIdsUpdated"
                >
                    <template #default="{ open }">
                        <div class="inline-flex items-center gap-2 border border-gray-200 rounded-lg px-4 py-2 bg-white hover:bg-gray-50 transition-colors cursor-pointer" @click="open">
                            <div class="flex flex-col leading-tight">
                                <span class="text-sm font-medium text-primary-700">{{ $t('views.reports.reportsPage.selectFeedReconciliationsButton') }}</span>
                                <span class="text-xs text-gray-400 tabular-nums">
                                    <template v-if="state.reconciliationIds.length > 0">{{ state.reconciliationIds.length }} {{ $t('views.reports.reportsPage.feedReconciliationsHint') }}</template>
                                    <template v-else>{{ $t('views.reports.reportsPage.feedReconciliationsNoneSelected') }}</template>
                                </span>
                            </div>
                        </div>
                    </template>
                </SelectFeedReconciliations>
            </template>
            <div class="flex flex-wrap items-center justify-end gap-4">
                <CustomButton class="max-w-32 !w-auto px-6" color-preset="secondary" @click="onResetClicked">
                    {{ $t('views.reports.resetButtonLabel') }}
                </CustomButton>
                <EControlModal :state="state" :comment-difference="commentDifference"></EControlModal>
                <ContentOptionsModal :state="state"></ContentOptionsModal>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Tab, Tabs } from '@makeabledk/vue-ui/components/tabs';
import CustomButton from '@/components/ui/CustomButton.vue';
import ContentOptionsModal from '@/views/dashboard/reports/reportsPage/ContentOptionsModal.vue';
import EControlModal from '@/views/dashboard/reports/reportsPage/EControlModal.vue';
import SelectFeedReconciliations from '@/views/dashboard/reports/reportsPage/SelectFeedReconciliations.vue';
import PigsStatusGraph from '@/views/dashboard/reports/reportsPage/PigsStatusGraph.vue';
import ReportsEntriesTable from '@/views/dashboard/reports/reportsPage/ReportsEntriesTable.vue';
import ReportsDeparturesTable from '@/views/dashboard/reports/reportsPage/ReportsDeparturesTable.vue';
import MainViewCard from '@/components/common/MainViewCard.vue';
import { ReportViewState, Report, RecordResponse } from '@/types';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';

export default defineComponent({
    components: { CustomButton, ContentOptionsModal, EControlModal, SelectFeedReconciliations, PigsStatusGraph, MainViewCard, ReportsEntriesTable, ReportsDeparturesTable, Tab, Tabs },
    props: {
        state: {
            type: Object as () => ReportViewState,
            required: true,
        },
    },
    emits: ['reset'],
    data() {
        return {
            activeReport: this.$props.state.reports[0],
            activeTabIndex: 0,
            commentDifference: '',
        };
    },
    computed: {
        emptyEntryTableRows() {
            return Math.max(this.activeReport.departuresTable.items.length - this.activeReport.entriesTable.items.length, 0);
        },
    },
    created() {
        store.dispatch(ActionType.GetRecordTypes).then(this.fetchRecords);
    },
    methods: {
        onTabClicked(report: Report, index: number) {
            this.activeReport = report;
            this.activeTabIndex = index;
            this.fetchRecords();
        },
        onResetClicked() {
            this.$emit('reset');
        },
        async fetchRecords() {
            const recordResponse: RecordResponse = await store.dispatch(ActionType.GetReportRecords, {
                options: { ignoreCache: true },
                form: {
                    siteIds: this.activeReport.site_ids,
                    period: `from=${this.activeReport.from}&to=${this.activeReport.to}`,
                },
            });

            this.activeReport.records = recordResponse.data.map((record) => {
                const recordType = store.state.recordTypes.find((currentRecordType) => currentRecordType.id === record.type_id);
                return { ...record, icon_url: recordType?.icon_url, name: recordType?.name };
            });
        },
        onCommentUpdated(comment: string) {
            this.commentDifference = comment;
        },
        onReconciliationIdsUpdated(newIds: number[]) {
            this.$props.state.reconciliationIds.splice(0, this.$props.state.reconciliationIds.length, ...newIds);
        },
    },
});
</script>

<style scoped>
.select-date-tabs nav a {
    border-width: 1px;
    border-radius: 0.5rem;
    @apply border-opacity-20;
    padding: 0.5rem 1.25rem 0.5rem 1.25rem;
    font-weight: 600;
    background-color: white;
    text-align: center;
}

.select-date-tabs nav a.--active {
    @apply bg-primary-800;
    @apply border-primary-700;
    color: white;
}
</style>
